import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const [allOrgs, setAllOrgs]           = useState([]);
  const [activeOrgId, setActiveOrgId]   = useState(() => localStorage.getItem('activeOrgId'));
  const [loading, setLoading]           = useState(true);
  // Which user the orgs in state belong to. `loading` starts true once, at page
  // load. Signing in from the login screen is not a page load, so without this the
  // app saw "no orgs, not loading" for a moment, opened the Setup wizard with a
  // blank form, and finishing it created a second org on top of the unfinished one.
  const loadedFor = useRef(null);

  const loadOrgs = async (userId) => {
    const { data, error } = await supabase
      .from('org_members')
      .select('*, organizations(*)')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) { setLoading(false); return; }
    if (!data?.length) {
      // A brand-new account with no org yet. Remember the user anyway, or every
      // token refresh would flash Loading and reset the wizard they are in.
      loadedFor.current = userId;
      setLoading(false);
      return;
    }

    setAllOrgs(data);

    // Prefer a fully set-up org for the initial view: an invited contractor
    // also owns an auto-created personal org (setup_complete=false) and must
    // not land in its Setup wizard by default. Only restore the stored org if
    // it is itself complete — a stale localStorage pointer to an incomplete org
    // should not override a complete org on fresh login.
    const stored  = localStorage.getItem('activeOrgId');
    const storedMember = data.find(m => m.org_id === stored);
    const storedIsComplete = storedMember && storedMember.organizations?.setup_complete !== false;
    const firstComplete = data.find(m => m.organizations?.setup_complete !== false);
    const initial = (storedIsComplete ? storedMember : (firstComplete ?? storedMember ?? data[0])).org_id;
    setActiveOrgId(initial);
    localStorage.setItem('activeOrgId', initial);
    loadedFor.current = userId;
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) loadOrgs(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        // A new person (or the same one after sign-out): hold the app on "Loading"
        // until their orgs are in. Token refreshes for the same user don't blank the screen.
        if (loadedFor.current !== session.user.id) setLoading(true);
        loadOrgs(session.user.id);
      } else {
        loadedFor.current = null;
        setAllOrgs([]);
        setActiveOrgId(null);
        localStorage.removeItem('activeOrgId');
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const setActiveOrg = (orgId) => {
    if (!allOrgs.find(m => m.org_id === orgId)) return;
    setActiveOrgId(orgId);
    localStorage.setItem('activeOrgId', orgId);
  };

  const activeMember = allOrgs.find(m => m.org_id === activeOrgId) ?? allOrgs[0] ?? null;
  const org = activeMember?.organizations ?? null;
  const orgMember = activeMember ? {
    id:                activeMember.id,
    org_id:            activeMember.org_id,
    role:              activeMember.role,
    seat_type:         activeMember.seat_type,
    compensation_rate: activeMember.compensation_rate,
    is_support_access: activeMember.is_support_access,
    expenses_enabled:  activeMember.expenses_enabled,
  } : null;

  const reload = async (userId) => {
    if (userId) await loadOrgs(userId);
    else {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await loadOrgs(session.user.id);
    }
  };

  return (
    <OrgContext.Provider value={{ org, orgMember, allOrgs, setActiveOrg, loading, reload }}>
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => useContext(OrgContext);
